// ============================================================
// store/stash-card-detail/pipeline.ts
//
// Pure builders that turn raw StashCard + Vendor + Stasher data
// into the presentation shapes consumed by the StashCard page.
// Every function in this file is side-effect-free — easy to test,
// easy to memoize via useMemo in hooks.ts.
// ============================================================

import { caseName } from '@stash/shared-types';
import type {
  StashCard,
  StashCardActivity,
  Stasher,
  StashCategory,
  Vendor,
  ReachChannel,
  ReachState,
  ReachTile,
  HistoryItem,
  HistoryKind,
  StasherChip,
  FeaturedNote,
} from './types';

// ───── Channel availability ─────

/** Does the vendor have a phone we can dial? */
function hasPhone(vendor: Vendor): boolean {
  if (vendor.businessPhone && vendor.businessPhone.trim()) return true;
  return (vendor.additionalContacts ?? []).some(
    c => 'phone' in c && !!c.phone.number?.trim(),
  );
}

function firstPhone(vendor: Vendor): string | undefined {
  if (vendor.businessPhone?.trim()) return vendor.businessPhone;
  const ac = (vendor.additionalContacts ?? []).find(c => 'phone' in c);
  return ac && 'phone' in ac ? ac.phone.number : undefined;
}

function firstEmail(vendor: Vendor): string | undefined {
  if (vendor.businessEmail?.trim()) return vendor.businessEmail;
  const ac = (vendor.additionalContacts ?? []).find(c => 'email' in c);
  return ac && 'email' in ac ? ac.email.email : undefined;
}

// ───── Activity → channel mapping ─────

// Keyed by the activityType case name (`{ call: {...} }` → 'call').
const ACTIVITY_TO_CHANNEL: Partial<Record<string, ReachChannel>> = {
  call: 'call',
  callCompleted: 'call',
  sms: 'text',
  email: 'email',
  website: 'web',
  map: 'map',
  share: 'share',
};

function channelForActivity(a: StashCardActivity): ReachChannel | null {
  return ACTIVITY_TO_CHANNEL[caseName(a.activityType)] ?? null;
}

// ───── Reach tile builders ─────

const KBD: Record<ReachChannel, string> = {
  call:  '⌘C',
  text:  '⌘T',
  email: '⌘E',
  web:   '⌘W',
  share: '⌘S',
  map:   '⌘M',
};

function makeCallTile(vendor: Vendor, count: number, lastWhen: string | null): ReachTile {
  const phone = firstPhone(vendor);
  return {
    channel: 'call',
    available: !!phone,
    target: vendor.contactName || vendor.companyName || 'Vendor',
    ...(phone ? { href: `tel:${phone.replace(/[^+\d]/g, '')}`, activityType: { call: { phone } } } : {}),
    kbd: KBD.call,
    verb: `Call ${vendor.contactName || vendor.companyName || ''}`.trim(),
    meta: phone ? `${phone}${lastWhen ? ` · last call ${lastWhen}` : ''}` : 'no phone on file',
    sub: count > 0 ? `${count} call${count === 1 ? '' : 's'}${lastWhen ? ` · last ${lastWhen}` : ''}` : 'first call',
    count,
  };
}

function makeTextTile(vendor: Vendor, count: number, lastWhen: string | null): ReachTile {
  const phone = firstPhone(vendor);
  return {
    channel: 'text',
    available: !!phone,
    target: vendor.contactName || vendor.companyName || 'Vendor',
    ...(phone ? { href: `sms:${phone.replace(/[^+\d]/g, '')}`, activityType: { sms: { phone } } } : {}),
    kbd: KBD.text,
    verb: `Text ${vendor.contactName || vendor.companyName || ''}`.trim(),
    meta: phone ? `${phone}${lastWhen ? ` · last text ${lastWhen}` : ''}` : 'no phone on file',
    sub: count > 0 ? `${count} message${count === 1 ? '' : 's'}` : 'no messages yet',
    count,
  };
}

function makeEmailTile(vendor: Vendor, count: number, _lastWhen: string | null): ReachTile {
  const email = firstEmail(vendor);
  return {
    channel: 'email',
    available: !!email,
    target: email ?? '',
    ...(email ? { href: `mailto:${email}`, activityType: { email: { address: email } } } : {}),
    kbd: KBD.email,
    verb: `Email ${vendor.contactName || vendor.companyName || ''}`.trim(),
    meta: email ?? 'no email on file',
    sub: count > 0 ? `${count} email${count === 1 ? '' : 's'}` : email ?? 'first email',
    count,
  };
}

function makeWebTile(vendor: Vendor, count: number, _lastWhen: string | null): ReachTile {
  const url = vendor.websiteUrl;
  // Strip protocol for display.
  const display = url ? url.replace(/^https?:\/\/(www\.)?/, '') : '';
  return {
    channel: 'web',
    available: !!url,
    target: display,
    ...(url ? { href: url, activityType: { website: { urlString: url } } } : {}),
    kbd: KBD.web,
    verb: `Visit ${display}`,
    meta: url ?? 'no website on file',
    sub: count > 0 ? `${count} visit${count === 1 ? '' : 's'}` : display || 'open site',
    count,
  };
}

function makeMapTile(vendor: Vendor, count: number, distanceMi: number | null): ReachTile {
  const addr = vendor.address;
  const addrStr = addr ? [addr.street, addr.city, addr.region].filter(Boolean).join(', ') : '';
  const mapsHref = addrStr ? `https://maps.apple.com/?address=${encodeURIComponent(addrStr)}` : undefined;
  return {
    channel: 'map',
    available: !!addr,
    target: addr ? addr.city : '',
    ...(mapsHref ? { href: mapsHref, activityType: { map: { formattedAddress: addrStr } } } : {}),
    kbd: KBD.map,
    verb: 'Get directions',
    meta: addr ? addrStr : 'no address on file',
    sub: distanceMi != null ? `${distanceMi.toFixed(1)} mi away` : (addr?.city ?? ''),
    count,
  };
}

function makeShareTile(stasherCount: number, count: number): ReachTile {
  const friendsHave = Math.max(0, stasherCount - 1);
  return {
    channel: 'share',
    available: true,
    target: 'friends',
    activityType: { share: {} },
    kbd: KBD.share,
    verb: 'Share Card',
    meta: friendsHave > 0 ? `${friendsHave} friend${friendsHave === 1 ? '' : 's'} already have` : 'invite a friend',
    sub: count > 0
      ? `${count} share${count === 1 ? '' : 's'}`
      : friendsHave > 0 ? `${friendsHave} friend${friendsHave === 1 ? '' : 's'} already have` : 'tell a friend',
    count,
  };
}

// ───── Relative time ─────

const MS = { minute: 60_000, hour: 3_600_000, day: 86_400_000, week: 604_800_000 };

export function relativeTime(date: Date, now = new Date()): string {
  const diff = now.getTime() - date.getTime();
  if (diff < MS.minute) return 'just now';
  if (diff < MS.hour) return `${Math.floor(diff / MS.minute)}m ago`;
  if (diff < MS.day) return `${Math.floor(diff / MS.hour)}h ago`;
  if (diff < MS.week) return `${Math.floor(diff / MS.day)}d ago`;
  if (diff < MS.week * 4) return `${Math.floor(diff / MS.week)}w ago`;
  if (diff < MS.day * 365) return `${Math.floor(diff / (MS.day * 30))}mo ago`;
  return `${Math.floor(diff / (MS.day * 365))}y ago`;
}

// ───── Reach pipeline ─────

/**
 * Compose the Reach bar for a card. Order of operations:
 *   1. Bucket activities by channel; track most-recent timestamp per channel.
 *   2. Build a tile for each channel; mark availability.
 *   3. Promote the most-recently-used channel as primary. Fall back to call
 *      (if phone exists) or web (otherwise).
 *   4. Order alts in stable preference: text · web · map · email · share,
 *      skipping unavailable channels and the promoted one.
 *   5. Trim alts to 3.
 */
export function buildReachState(
  card: StashCard,
  vendor: Vendor,
  stasherCount: number,
  distanceMi: number | null = null,
  now: Date = new Date(),
): ReachState {
  const activities = card.stashCardActivities ?? [];
  const counts: Record<ReachChannel, number> = {
    call: 0, text: 0, email: 0, web: 0, share: 0, map: 0,
  };
  const lastDates: Partial<Record<ReachChannel, Date>> = {};

  for (const a of activities) {
    const ch = channelForActivity(a);
    if (!ch) continue;
    counts[ch] += 1;
    const when = a.createdOn instanceof Date ? a.createdOn : new Date(a.createdOn as unknown as string);
    if (!lastDates[ch] || when > (lastDates[ch] as Date)) {
      lastDates[ch] = when;
    }
  }

  const tile = (ch: ReachChannel): ReachTile => {
    const last = lastDates[ch] ? relativeTime(lastDates[ch] as Date, now) : null;
    switch (ch) {
      case 'call':  return makeCallTile (vendor, counts.call,  last);
      case 'text':  return makeTextTile (vendor, counts.text,  last);
      case 'email': return makeEmailTile(vendor, counts.email, last);
      case 'web':   return makeWebTile  (vendor, counts.web,   last);
      case 'map':   return makeMapTile  (vendor, counts.map,   distanceMi);
      case 'share': return makeShareTile(stasherCount, counts.share);
    }
  };

  // Determine the promoted channel.
  let promoted: ReachChannel;
  // Find most-recent across available channels.
  const orderedByRecency = (['call', 'text', 'email', 'web', 'map', 'share'] as ReachChannel[])
    .filter(ch => lastDates[ch])
    .sort((a, b) => (lastDates[b] as Date).getTime() - (lastDates[a] as Date).getTime());
  if (orderedByRecency.length > 0) {
    promoted = orderedByRecency[0];
  } else if (hasPhone(vendor)) {
    // Per design decision: phone → email → web fallback when no history exists.
    promoted = 'call';
  } else if (firstEmail(vendor)) {
    promoted = 'email';
  } else if (vendor.websiteUrl) {
    promoted = 'web';
  } else {
    promoted = 'share'; // last-resort fallback (always available)
  }

  // Stable preference for alts.
  const altPreference: ReachChannel[] = ['call', 'text', 'web', 'email', 'map', 'share'];
  const alts = altPreference
    .filter(ch => ch !== promoted)
    .map(tile)
    .filter(t => t.available)
    .slice(0, 3);

  return { primary: tile(promoted), alts };
}

// ───── History pipeline ─────

// Keyed by the activityType case name (`{ call: {...} }` → 'call').
const ACTIVITY_TO_HISTORY_KIND: Partial<Record<string, HistoryKind>> = {
  call: 'call',
  callCompleted: 'call',
  sms: 'text',
  email: 'email',
  website: 'web',
  map: 'map',
  share: 'share',
};

const ACTIVITY_TITLE_VERB: Partial<Record<string, (target: string) => string>> = {
  call:          t => `Called ${t}`,
  callCompleted: () => `Call ended`,
  sms:           t => `Messaged ${t}`,
  email:         t => `Emailed ${t}`,
  website:       () => `Viewed website`,
  map:           () => `Mapped`,
  social:        () => `Viewed social`,
  share:         () => `Shared Card`,
};

/**
 * Merge `StashCardActivity[]` and `StashCardNote[]` into a single timeline,
 * sorted newest-first. Activities authored by the current user get
 * "You" as author label; others use the Stasher's display name.
 */
export function buildHistory(
  card: StashCard,
  vendor: Vendor,
  stashers: Stasher[],
  currentUserStasherId: string,
): HistoryItem[] {
  const target = vendor.contactName || vendor.companyName || 'vendor';
  const stasherById = new Map(stashers.map(s => [s.recordID, s]));

  const fromActivities: HistoryItem[] = (card.stashCardActivities ?? []).flatMap(a => {
    const activityCase = caseName(a.activityType);
    const kind = ACTIVITY_TO_HISTORY_KIND[activityCase];
    if (!kind) return [];
    const isYou = a.stasherId === currentUserStasherId;
    const stasher = stasherById.get(a.stasherId);
    const verb = ACTIVITY_TITLE_VERB[activityCase];
    const occurredAtDate = a.createdOn instanceof Date ? a.createdOn : new Date(a.createdOn as unknown as string);
    return [{
      id: a.id,
      kind,
      title: verb ? verb(target) : a.title || 'Activity',
      body: a.detail || '',
      occurredAt: occurredAtDate.toISOString(),
      author: isYou ? 'You' : (stasher ? `${stasher.firstName} ${stasher.lastName}` : 'Someone'),
      ...(stasher?.profilePhotoIconUrl ? { authorAvatarUrl: stasher.profilePhotoIconUrl } : {}),
    }];
  });

  const fromNotes: HistoryItem[] = (card.stashCardNotes ?? []).map(n => {
    // Notes don't carry a stasherId directly; we attribute to current user.
    const occurredAtDate = n.createdOn instanceof Date ? n.createdOn : new Date(n.createdOn as unknown as string);
    return {
      id: n.recordID,
      kind: 'note' as const,
      title: n.title || 'Note',
      body: n.detail || '',
      occurredAt: occurredAtDate.toISOString(),
      author: 'You',
    };
  });

  return [...fromActivities, ...fromNotes].sort((a, b) =>
    b.occurredAt.localeCompare(a.occurredAt),
  );
}

// ───── Stashers strip pipeline ─────

/**
 * Resolve `vendor.stashCardStasherIds` to a sorted list of StasherChip values.
 * Friends-of-current-user appear first; the chip carrying the most-recent
 * featured note is flagged so the UI can pull-quote them.
 */
export function buildStashersStrip(
  vendor: Vendor,
  allStashers: Stasher[],
  friendIds: Set<string>,
  currentUserStasherId: string,
  featuredNoteAuthorId: string | null,
): StasherChip[] {
  const stasherById = new Map(allStashers.map(s => [s.recordID, s]));
  const ids = vendor.stashCardStasherIds ?? [];
  const chips = ids
    .map(id => stasherById.get(id))
    .filter((s): s is Stasher => !!s)
    .filter(s => s.recordID !== currentUserStasherId);

  // Sort: friends first, then alphabetical.
  chips.sort((a, b) => {
    const aF = friendIds.has(a.recordID) ? 0 : 1;
    const bF = friendIds.has(b.recordID) ? 0 : 1;
    if (aF !== bF) return aF - bF;
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  });

  return chips.map(s => ({
    id: s.recordID,
    name: `${s.firstName} ${s.lastName}`.trim(),
    initials: `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`.toUpperCase() || '?',
    ...(s.profilePhotoIconUrl ? { avatarUrl: s.profilePhotoIconUrl } : {}),
    isFriend: friendIds.has(s.recordID),
    hasFeaturedNote: s.recordID === featuredNoteAuthorId,
  }));
}

// ───── Featured note pipeline ─────

/**
 * Pick the pull-quote note. Rule: the most-recent public note from any
 * Stasher who's stashed this vendor and isn't the current user.
 *
 * Returns null when no qualifying note exists.
 */
export function buildFeaturedNote(
  card: StashCard,
  stashers: Stasher[],
  currentUserStasherId: string,
  now: Date = new Date(),
): { note: FeaturedNote; authorStasherId: string } | null {
  const candidate = (card.stashCardNotes ?? [])
    .filter(n => !n.isPrivate)
    .filter(n => !!n.detail?.trim())
    .sort((a, b) => {
      const ad = a.createdOn instanceof Date ? a.createdOn : new Date(a.createdOn as unknown as string);
      const bd = b.createdOn instanceof Date ? b.createdOn : new Date(b.createdOn as unknown as string);
      return bd.getTime() - ad.getTime();
    })[0];

  if (!candidate) return null;

  // For now, attribute to the card's stasherId (the original recommender).
  if (card.stasherId === currentUserStasherId) return null;
  const author = stashers.find(s => s.recordID === card.stasherId);
  if (!author) return null;

  const occurredAtDate = candidate.createdOn instanceof Date
    ? candidate.createdOn
    : new Date(candidate.createdOn as unknown as string);

  return {
    note: {
      body: candidate.detail,
      authorName: `${author.firstName} ${author.lastName}`.trim(),
      ...(author.profilePhotoIconUrl ? { authorAvatarUrl: author.profilePhotoIconUrl } : {}),
      ageLabel: relativeTime(occurredAtDate, now),
    },
    authorStasherId: author.recordID,
  };
}

// ───── Category breadcrumb pipeline ─────

/**
 * Walk the category tree from a leaf to the root, returning ["Root", "Sub", ...].
 * Same pattern as `stash-scoped/pipeline.ts` so the breadcrumb formatting matches
 * what the StashCardList row shows.
 */
export function buildCategoryBreadcrumb(
  category: StashCategory,
  categories: StashCategory[],
): string[] {
  const byId = new Map(categories.map(c => [c.recordID, c]));
  const trail: string[] = [];
  let cursor: StashCategory | undefined = category;
  // Defend against cycles.
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor.recordID)) {
    trail.unshift(cursor.name);
    seen.add(cursor.recordID);
    cursor = cursor.parentCategoryID ? byId.get(cursor.parentCategoryID) : undefined;
  }
  return trail;
}
